<template>
    <div class="school-map-view" id="school-map-view">
        <v-flex class="d-flex vue2leaflet-map-wrapper">
        <l-map
          :zoom="zoom"
          :center="center"
          :options="mapOptions"
          style="height: 80%"
          ref="schoolMap"
          :bounds="bounds"
          @update:center="centerUpdate"
          :maxBounds="maxBounds"
        >
          <l-tile-layer
                  :url="url"
                  :attribution="attribution"
          />
          <l-control position="bottomleft">
            <v-flex xs12 class="school-map-legend">
              <span class="school-map-legend-title">{{ $t('Score out of 10') }}</span>
              <v-layout row justify-center align-center class="layout-wrapper">
                <v-flex xs3 class="box school-map-legend-red">
                  <div class="school-map-legend-label">
                  0 - 4
                  </div>
                </v-flex>
                <v-flex xs3 class="box school-map-legend-orange">
                  <div class="school-map-legend-label">
                  4.1 - 5.9
                  </div>
                </v-flex>
                <v-flex xs3 class="box school-map-legend-yellow">
                  <div class="school-map-legend-label">
                  6 - 7.5
                  </div>
                </v-flex>
                <v-flex xs3 class="box school-map-legend-green">
                  <div class="school-map-legend-label">
                  7.6 - 10
                  </div>
                </v-flex>
              </v-layout>
            </v-flex>
          </l-control>

          <l-feature-group ref="features">
            <l-popup>
              <v-flex class="school-count">{{schoolGroup.length}}</v-flex>
              <v-flex class="school-group-item" v-for="school in schoolGroup" :key="'school-group-item-' + school.id" >
                <v-container grid-list-xs>
                    <v-layout row wrap justify-space-between>
                      <v-flex xs8 class="school-name">
                        {{ school.name }}
                      </v-flex>
                      <v-flex xs4 class="score">
                        <v-layout row wrap>
                          <v-flex xs12 class="label">{{ $t('Score') }}</v-flex>
                          <v-flex xs12
                            :class="[
                                'field',
                                school.score >= 0 && school.score < 4.1 ? 'score_color_1': '',
                                school.score >= 4.1 && school.score < 6 ? 'score_color_2': '',
                                school.score >= 6 && school.score < 7.6 ? 'score_color_3': '',
                                school.score >= 7.6 && school.score <= 10 ? 'score_color_4': ''
                              ]"
                          >{{ school.score !== 'n/a' ? (parseFloat(school.score).toFixed(1)) : 'n/a' }}</v-flex>
                        </v-layout>
                      </v-flex>
                      <v-flex xs8 class="type">
                        <span>{{ school.type }}</span>
                      </v-flex>
                      <v-flex xs4 class="rank">
                        <v-layout row wrap>
                          <v-flex xs12 class="label">{{ $t('Rank') }}</v-flex>
                          <v-flex xs12 class="field">{{ school.ranking }}/{{ schoolTotal }} </v-flex>
                        </v-layout>
                      </v-flex>
                      <v-flex class="learn-more" :class="markerPopupBtnClass()">
                        <v-btn round>
                        <router-link @click.native="scrollToTop();$gtag.event('Learn More Map');"
                                     :to="{name: 'schoolpage', params: {province: school.province, level: school.level, id: school.id }}"
                        >{{ $t('Learn more') }}</router-link>
                        </v-btn>
                      </v-flex>
                      <v-flex xs6 class="add-to-compare">
                        <a href="#" @click.stop.prevent="addToCompare(school);$gtag.event('Add to Compare Map');">{{ $t('Add to compare') }}</a>
                      </v-flex>
                    </v-layout>
                  </v-container>
              </v-flex>
            </l-popup>
          </l-feature-group>

          <!-- Schools marker -->
          <v-marker-cluster
            ref="clusterRef"
            :options="clusterOptions"
            @clusterclick="openGroupPopup($event);$gtag.event('Map Pin');"
          >
            <l-marker
              v-for="school in $store.state.searchSchoolListFiltered"
              :options="{school: school}"
              :key="'school-' + school.id"
              :lat-lng="school.location"
              :icon="school.icon"
              @click="openSinglePopup($event);$gtag.event('Map Pin');"
            ></l-marker>
          </v-marker-cluster>
        </l-map>
        <SchoolMapSideBar class="hidden-sm-and-down"/>
      </v-flex>
    </div>
</template>
<script>
  import { LMap, LTileLayer, LMarker, LPopup, LControl, LFeatureGroup } from 'vue2-leaflet';
  import L from 'leaflet';
  // The gesture handling is imported and used in a function directly by leaflet.
  // eslint-disable-next-line
  import { GestureHandling } from "leaflet-gesture-handling";
  import Vuex from 'vuex';
  import RepositoryFactory from "@/services/RepositoryFactory";
  import SchoolMapSideBar from '@/components/SchoolMap/SchoolMapSideBar';

  const StorageService = RepositoryFactory.get('storage');
  const RADIUS_KM = 10;
  const ONE_DEGREE_KM = 111.045;

  export default {
    name: 'SchoolMapView',
    components: {
      LMap,
      LTileLayer,
      LMarker,
      LPopup,
      LControl,
      LFeatureGroup,
      SchoolMapSideBar
    },
    data () {
      let data = {
        isMobile: false,
        schoolMap: null,
        zoom: 10,
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        currentZoom: 11.5,
        currentMapLocation: null,
        mapOptions: {
          zoomControl: true,
          zoomSnap: 0.5,
          maxZoom: 19,
          minZoom: 4,
          gestureHandling: true,
        },
        maxBounds: L.latLngBounds([[69.815, -141.273], [40.455, -52.225]]),
        clusterOptions: {
          zoomToBoundsOnClick: false,
          showCoverageOnHover: false
        },
        latLngBounds: [],
        popupRawHmlt: '',
        schoolGroup: []
      };
      return data;
    },
    mounted: function() {

      this.schoolMap = this.$refs.schoolMap ? this.$refs.schoolMap.mapObject : null;
      this.$nextTick(() => {
        this.schoolMap = this.$refs.schoolMap ? this.$refs.schoolMap.mapObject : null;
        if (this.schoolMap) {
          this.$refs.schoolMap.mapObject.on('move', () => {
            setTimeout(()=>{
              this.$store.commit('CURRENT_MAP_LOCATION', this.$refs.schoolMap.mapObject.getCenter());
              this.$store.commit('CURRENT_MAP_ZOOM', this.$refs.schoolMap.mapObject.getZoom());
            }, 0);
          });
          this.checkIsMobile();
          if (this.isMobile) {
            this.schoolMap.zoomControl.remove();
          }
          L.tileLayer.provider('CartoDB.Voyager').addTo(this.schoolMap);
        }

      });

      this.$root.$on('centerMap', () => {
        this.resetMap();
      });

      this.$store.subscribe((mutation, state) => {
        if (this.schoolMap != null) {
          if (mutation.type === 'CURRENT_LANGUAGE') {
            if (state.currentLanguage === 'fr') {
              this.schoolMap.options.gestureHandlingOptions = {
                duration: 1000,
                text: {
                  touch: "Utilisez les deux doigts pour faire défiler la carte",
                  scroll: "Utilisez ctrl + faites défiler pour zoomer sur la carte",
                  scrollMac: "Utilisez \u2318 + faites défiler pour zoomer sur la carte"
                }
              }
            } else {
              delete(this.schoolMap.options.gestureHandlingOptions);
            }
            this.schoolMap.gestureHandling.disable();
            this.schoolMap.gestureHandling.enable();
          }
        }
      });
    },
    computed: {
      ...Vuex.mapState({
        currentLanguage: state => state.currentLanguage
      }),
      schoolInMapProp: {
        get() {
          return this.schoolInMap;
        },
        set(data) {
          this.schoolInMap = data;
        }
      },
      center: {
        get() {
          let vm = this;
          let currentMapLocation = this.$store.state.currentMapLocation;

          vm.zoom = this.$store.state.currentMapZoom;

          let userAddress = JSON.parse(StorageService.getItem('userAddress'));
          let userLocation = JSON.parse(StorageService.getItem('userLocation'));
          let latLng = [54.7227052, -113.7222159];
          if (currentMapLocation.lat && currentMapLocation.lng) {
            latLng = [currentMapLocation.lat, currentMapLocation.lng];
          }
          else if (userAddress && userAddress.country_code === 'ca' && userAddress.province === vm.$store.state.currentMapProvince) {
            // Calculate northwest corner and southeast corner within RADIUS_KM from user location.
            vm.latLngBounds = {};
            let dLat = RADIUS_KM / ONE_DEGREE_KM;
            let dLng = dLat / Math.cos(userLocation.lat);
            vm.latLngBounds = {
              corner1: L.latLng(userLocation.lat + dLat, userLocation.lng - dLng),
              corner2: L.latLng(userLocation.lat - dLat, userLocation.lng + dLng)
            };
            latLng = [userLocation.lat, userLocation.lng];
          }
          else {
            vm.zoom = 5;
            latLng = [54.7227052, -113.7222159];
          }
          this.centerUpdate();
          return L.latLng.apply(this,latLng);
        }
      },
      schoolTotal() {
        return this.$store.state.initialSearchSchoolListFilteredCount;
      },
      bounds: function() {
        return typeof this.latLngBounds != 'object'
          ? L.latLngBounds(this.latLngBounds)
          : L.latLngBounds(this.latLngBounds.corner1, this.latLngBounds.corner2);
      },
      compareSchoolTotal() {
        return this.$store.state.compareSchool.length;
      },
      switcherMapList() {
        return this.$store.state.switcherMapList;
      }
    },
    watch: {
      switcherMapList(newVal) {
        if (newVal == 'map') {
          /*console.info({
            "element-model": 'Switcher Map/List',
            "action: ": "Switch to Map View",
            time: Math.floor(Date.now() / 1000)
          },"EVENT");*/
          this.$refs['schoolMap'].mapObject._onResize();
        }
      }
    },
    methods: {
      getLanguage() {
        return localStorage.getItem('language') || 'en';
      },
      markerPopupBtnClass() {
        const isEnglish = this.getLanguage() === 'en';

        return {
          'xs6': isEnglish,
          'py-3': !isEnglish,
          'xs12': !isEnglish,
        };
      },
      scrollToTop() {
        window.scrollTo(0,0);
      },
      centerUpdate () {
        if (this.$store.state.switcherMapList == 'map' && this.$store.state.isFront) {
          let schoolsInMap = [];
          let schools = this.$store.state.searchSchoolListFiltered;
          // Do sidebar update in a separate process.
          setTimeout(() => {
            for (let i = schools.length - 1; i >= 0; i--) {
              if (this.schoolMap.getBounds().contains(schools[i].location)) {
                schoolsInMap.push(schools[i]);
              }
            }
            this.$store.commit('CURRENT_SCHOOLS_IN_MAP', schoolsInMap);
          }, 0);
        }
      },
      resetMap() {
        let schools = this.$store.state.searchSchoolListFiltered;

        setTimeout(() => {
          var latLngBounds = [];
          for(let i = schools.length - 1; i >= 0; i--) {
            latLngBounds.push(schools[i].location);
          }
          this.$store.commit('CURRENT_SCHOOLS_IN_MAP', schools);

          if(this.schoolMap) {
            this.schoolMap.fitBounds(latLngBounds);
            //this.schoolMap.fitBounds(latLngBounds);
            this.schoolMap.closePopup();
          }
        }, 0);
      },
      sortSchoolsByName(schools) {
        return schools.sort(function (a, b) {
          if (a.name > b.name) {
            return 1;
          }
          if (a.name < b.name) {
            return -1;
          }
          return 0;
        });
      },
      openGroupPopup: function (e) {
        let child = e.layer.getAllChildMarkers();
        let schoolsGroup = [];
        for (let i = child.length - 1; i >= 0; i--) {
          schoolsGroup.push(child[i].options.school);
        }
        this.schoolGroup = this.sortSchoolsByName(schoolsGroup);
        let latlng = [e.layer._latlng.lat, e.layer._latlng.lng];
        this.$refs.features.mapObject.openPopup(latlng);
        let marker_point = this.schoolMap.latLngToLayerPoint(e.layer._latlng);
        let center_coordinate = this.schoolMap.layerPointToLatLng(new L.point(marker_point.x, marker_point.y - 100));
        this.schoolMap.setView(center_coordinate);
      },
      openSinglePopup: function (e) {
        let vm = this;
        let schoolsGroup = [];
        schoolsGroup.push(e.sourceTarget.options.school);
        this.schoolGroup = this.sortSchoolsByName(schoolsGroup);
        let latlng = [e.latlng.lat, e.latlng.lng];
        vm.$refs.features.mapObject.openPopup(latlng);
        let marker_point = this.schoolMap.latLngToLayerPoint(e.latlng);
        let center_coordinate = this.schoolMap.layerPointToLatLng(new L.point(marker_point.x, marker_point.y));
        this.schoolMap.setView(center_coordinate);
      },
      addToCompare(school) {
        let schools = this.$store.state.compareSchool;
        if (
          (schools.filter(function(e) { return e.id === school.id; }).length == 0 ||
          schools.length == 0
          ) &&
          schools.length < 6
          ) {
          schools.push(school);
          this.$store.commit('SET_COMPARE_SCHOOL', schools);
        }
      },
      checkIsMobile() {
        this.isMobile = parseInt(document.documentElement.clientWidth) < 960;
      }
    }
  };
</script>
